import {
  getAnalytics,
  logEvent as logAnalyticsEvent,
  setUserProperties,
  type Analytics,
} from "firebase/analytics";
import { app } from "../firebase";
import { getCurrentUserId } from "./authenticationService";
import { getUTMParametersForAnalytics } from "./utmService";
import { getStoredConsent } from "./cookieConsentService";
import { getStoredRegionInfo } from "./regionService";
import type { RegionBucket } from "./regionService";
import {
  resolveAnalyticsReadiness,
  type AnalyticsDispatchResult,
  type AnalyticsReadinessStatus,
} from "./analyticsRecoveryPolicy";
import {
  getAnalyticsRuntimeSnapshot,
  markAnalyticsRuntimeInitialized,
  markGoogleAdsConfigured,
  recordAnalyticsDispatchAttempt,
} from "./analyticsRuntimeService";

export type {
  AnalyticsDispatchResult,
  AnalyticsReadinessStatus,
} from "./analyticsRecoveryPolicy";

const onboardingStepVisitedSuffix = "_visited";
const onboardingStepCompletedSuffix = "_completed";
const DIAGNOSTIC_SAMPLE_RATE = 0.03;
const DIAGNOSTIC_DEBOUNCE_MS = 60_000;
const GOOGLE_ADS_DEFAULT_TAG_ID = "AW-18086217163";
const GTAG_SCRIPT_URL_FRAGMENT = "googletagmanager.com/gtag/js";
const GTAG_DATA_LAYER_PARAM = "l=dataLayer";
const TRANSPORT_WARNING_DEBOUNCE_MS = 120_000;

type ConfiguredAnalytics = {
  analytics: Analytics | null;
  status: AnalyticsReadinessStatus;
  reason?: string;
  regionBucket?: RegionBucket;
};

let lastDiagnosticKey: string | null = null;
let lastDiagnosticAt = 0;
let configuredGoogleAdsTagId: string | null = null;
let didWarnMissingGtagForAds = false;
let didWarnDuplicateGtagScripts = false;
let didWarnMissingDataLayerMarker = false;
let lastTransportWarningAt = 0;

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
}

function shouldSample(status: AnalyticsReadinessStatus): boolean {
  if (status === "blocked_by_consent") {
    return Math.random() <= DIAGNOSTIC_SAMPLE_RATE;
  }
  return true;
}

function emitAnalyticsDiagnostic(result: AnalyticsDispatchResult): void {
  if (result.status === "ready" || !shouldSample(result.status)) {
    return;
  }

  const diagnosticKey = `${result.status}:${result.reason ?? ""}`;
  const now = Date.now();
  if (
    lastDiagnosticKey === diagnosticKey &&
    now - lastDiagnosticAt < DIAGNOSTIC_DEBOUNCE_MS
  ) {
    return;
  }

  lastDiagnosticKey = diagnosticKey;
  lastDiagnosticAt = now;

  console.info("[analytics] dispatch blocked", {
    status: result.status,
    reason: result.reason ?? "unspecified",
  });
}

type GoogleAdsWindow = Window & {
  gtag?: (...args: unknown[]) => void;
};

type ScriptInspectorDocument = {
  getElementsByTagName: (
    tagName: string
  ) => ArrayLike<{ src?: string | null }>;
};

export type GtagScriptDiagnostics = {
  totalGtagScripts: number;
  dataLayerTaggedScripts: number;
  duplicateScriptsDetected: boolean;
  missingDataLayerTag: boolean;
};

export type GoogleAdsConfigResult = {
  nextConfiguredTagId: string | null;
  didConfigure: boolean;
  reason:
    | "not_prod"
    | "missing_tag_id"
    | "missing_gtag_function"
    | "already_configured"
    | "configured";
  diagnostics: GtagScriptDiagnostics;
};

type GoogleAdsConfigDependencies = {
  isProd: boolean;
  runtimeWindow: GoogleAdsWindow | null;
  documentRef: ScriptInspectorDocument | null;
  tagId: string | null;
};

function buildDefaultGtagScriptDiagnostics(): GtagScriptDiagnostics {
  return {
    totalGtagScripts: 0,
    dataLayerTaggedScripts: 0,
    duplicateScriptsDetected: false,
    missingDataLayerTag: false,
  };
}

export function collectGtagScriptDiagnostics(
  documentRef: ScriptInspectorDocument | null
): GtagScriptDiagnostics {
  if (!documentRef) {
    return buildDefaultGtagScriptDiagnostics();
  }

  const scriptTags = documentRef.getElementsByTagName("script");
  let totalGtagScripts = 0;
  let dataLayerTaggedScripts = 0;

  for (let index = 0; index < scriptTags.length; index += 1) {
    const scriptSrc = scriptTags[index]?.src ?? "";
    if (!scriptSrc.includes(GTAG_SCRIPT_URL_FRAGMENT)) {
      continue;
    }

    totalGtagScripts += 1;
    if (scriptSrc.includes(GTAG_DATA_LAYER_PARAM)) {
      dataLayerTaggedScripts += 1;
    }
  }

  return {
    totalGtagScripts,
    dataLayerTaggedScripts,
    duplicateScriptsDetected: totalGtagScripts > 1,
    missingDataLayerTag:
      totalGtagScripts > 0 && dataLayerTaggedScripts === 0,
  };
}

export function resolveGoogleAdsTagId(): string | null {
  const override = import.meta.env.VITE_GOOGLE_ADS_TAG_ID?.trim();
  if (override) {
    return override;
  }
  return GOOGLE_ADS_DEFAULT_TAG_ID;
}

export function configureGoogleAdsTagWithUnifiedRuntime(
  configuredTagId: string | null,
  dependencies: GoogleAdsConfigDependencies
): GoogleAdsConfigResult {
  const diagnostics = collectGtagScriptDiagnostics(dependencies.documentRef);
  if (!dependencies.isProd) {
    return {
      nextConfiguredTagId: configuredTagId,
      didConfigure: false,
      reason: "not_prod",
      diagnostics,
    };
  }

  if (!dependencies.tagId) {
    return {
      nextConfiguredTagId: configuredTagId,
      didConfigure: false,
      reason: "missing_tag_id",
      diagnostics,
    };
  }

  if (
    !dependencies.runtimeWindow ||
    typeof dependencies.runtimeWindow.gtag !== "function"
  ) {
    return {
      nextConfiguredTagId: configuredTagId,
      didConfigure: false,
      reason: "missing_gtag_function",
      diagnostics,
    };
  }

  if (configuredTagId === dependencies.tagId) {
    return {
      nextConfiguredTagId: configuredTagId,
      didConfigure: false,
      reason: "already_configured",
      diagnostics,
    };
  }

  // The params object must be present: Firebase's gtag wrapper forwards our
  // arguments as gtag("config", id, undefined), and gtag.js treats an explicit
  // undefined third argument as the params object, later crashing with
  // "Cannot read properties of undefined (reading 'is_legacy_loaded')" and
  // aborting the Google Ads tag load.
  dependencies.runtimeWindow.gtag("config", dependencies.tagId, {});
  return {
    nextConfiguredTagId: dependencies.tagId,
    didConfigure: true,
    reason: "configured",
    diagnostics,
  };
}

function emitGtagScriptDiagnostics(diagnostics: GtagScriptDiagnostics): void {
  if (diagnostics.duplicateScriptsDetected && !didWarnDuplicateGtagScripts) {
    didWarnDuplicateGtagScripts = true;
    console.warn(
      "[analytics] detected multiple gtag.js scripts; runtime contention may cause unstable analytics",
      {
        totalGtagScripts: diagnostics.totalGtagScripts,
        dataLayerTaggedScripts: diagnostics.dataLayerTaggedScripts,
      }
    );
  }

  if (diagnostics.missingDataLayerTag && !didWarnMissingDataLayerMarker) {
    didWarnMissingDataLayerMarker = true;
    console.warn(
      "[analytics] gtag.js script missing dataLayer marker; Firebase script dedupe may fail",
      {
        totalGtagScripts: diagnostics.totalGtagScripts,
      }
    );
  }
}

function ensureGoogleAdsConfiguredThroughAnalytics(): void {
  const configResult = configureGoogleAdsTagWithUnifiedRuntime(
    configuredGoogleAdsTagId,
    {
      isProd: import.meta.env.PROD,
      runtimeWindow:
        typeof window === "undefined" ? null : (window as GoogleAdsWindow),
      documentRef: typeof document === "undefined" ? null : document,
      tagId: resolveGoogleAdsTagId(),
    }
  );

  emitGtagScriptDiagnostics(configResult.diagnostics);

  if (configResult.reason === "missing_gtag_function") {
    if (!didWarnMissingGtagForAds) {
      console.info(
        "[analytics] google ads config deferred; unified gtag runtime not ready"
      );
      didWarnMissingGtagForAds = true;
    }
    return;
  }

  didWarnMissingGtagForAds = false;
  configuredGoogleAdsTagId = configResult.nextConfiguredTagId;

  if (!configResult.didConfigure || !configResult.nextConfiguredTagId) {
    return;
  }

  markGoogleAdsConfigured(configResult.nextConfiguredTagId);
}

function emitTransportHealthDiagnostic(result: AnalyticsDispatchResult): void {
  if (result.transportHealth !== "unhealthy") {
    return;
  }
  const now = Date.now();
  if (now - lastTransportWarningAt < TRANSPORT_WARNING_DEBOUNCE_MS) {
    return;
  }
  lastTransportWarningAt = now;
  console.warn("[analytics] transport health warning", {
    transportHealth: result.transportHealth,
    transportReason: result.transportReason ?? result.reason ?? "unspecified",
  });
}

function getConfiguredAnalytics(): ConfiguredAnalytics {
  const consent = getStoredConsent();
  const region = getStoredRegionInfo();
  const regionBucket = region?.regionBucket;
  const readiness = resolveAnalyticsReadiness({
    isDev: import.meta.env.DEV,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    regionBucket,
    consent,
  });
  if (readiness.status !== "ready" || !regionBucket) {
    return {
      analytics: null,
      status: readiness.status,
      reason: readiness.reason,
      regionBucket,
    };
  }

  try {
    const analytics = getAnalytics(app);
    markAnalyticsRuntimeInitialized();
    ensureGoogleAdsConfiguredThroughAnalytics();
    const userId = getCurrentUserId();
    if (userId) {
      setUserProperties(analytics, {
        user_webonboarding_id: userId,
      });
    }
    return {
      analytics,
      status: "ready",
      regionBucket,
    };
  } catch (error) {
    return {
      analytics: null,
      status: "init_error",
      reason: describeError(error),
      regionBucket,
    };
  }
}

function dispatchWithConfiguredAnalytics(
  eventName: string,
  sender: (analytics: Analytics) => void
): AnalyticsDispatchResult {
  const configured = getConfiguredAnalytics();
  if (!configured.analytics) {
    const result: AnalyticsDispatchResult = {
      sent: false,
      status: configured.status,
      reason: configured.reason,
      runtimeInitialized: false,
      transportHealth: "unknown",
    };
    emitAnalyticsDiagnostic(result);
    return result;
  }

  try {
    sender(configured.analytics);
    recordAnalyticsDispatchAttempt(eventName);
    const runtimeSnapshot = getAnalyticsRuntimeSnapshot();
    const result: AnalyticsDispatchResult = {
      sent: true,
      status: "ready",
      runtimeInitialized: runtimeSnapshot.runtimeInitialized,
      transportHealth: runtimeSnapshot.transportHealth,
      transportReason: runtimeSnapshot.transportReason,
    };
    emitTransportHealthDiagnostic(result);
    return result;
  } catch (error) {
    const result: AnalyticsDispatchResult = {
      sent: false,
      status: "init_error",
      reason: describeError(error),
      runtimeInitialized: true,
      transportHealth: getAnalyticsRuntimeSnapshot().transportHealth,
    };
    emitAnalyticsDiagnostic(result);
    return result;
  }
}

export function getAnalyticsDispatchReadiness(): AnalyticsDispatchResult {
  const configured = getConfiguredAnalytics();
  const runtimeSnapshot = getAnalyticsRuntimeSnapshot();
  return {
    sent: configured.status === "ready",
    status: configured.status,
    reason: configured.reason,
    runtimeInitialized: runtimeSnapshot.runtimeInitialized,
    transportHealth: runtimeSnapshot.transportHealth,
    transportReason: runtimeSnapshot.transportReason,
  };
}

/**
 * Get base event parameters that should be included in all analytics events
 * Includes user ID and UTM parameters
 */
function getBaseEventParameters(): Record<string, unknown> {
  const userId = getCurrentUserId();
  const utmParams = getUTMParametersForAnalytics();

  return {
    user_webonboarding_id: userId,
    ...utmParams,
  };
}

export function convertSlugToAnalyticsEvent(slug?: string): string | undefined {
  if (!slug) {
    return undefined;
  }
  return (
    slug.replace(/-/g, "_").substring(0, 30) + onboardingStepCompletedSuffix
  );
}

export async function logEvent(
  eventName: string,
  eventParams: Record<string, unknown> | undefined = undefined
): Promise<AnalyticsDispatchResult> {
  const baseParams = getBaseEventParameters();
  return dispatchWithConfiguredAnalytics(eventName, (analytics) => {
    logAnalyticsEvent(analytics, eventName, {
      ...baseParams,
      ...eventParams,
    });
  });
}

export async function logScreenMounted(
  slug: string | undefined
): Promise<AnalyticsDispatchResult> {
  if (slug === undefined) {
    return {
      sent: false,
      status: "init_error",
      reason: "screen slug missing",
    };
  }

  const baseParams = getBaseEventParameters();
  const eventName =
    slug.replace(/-/g, "_").substring(0, 30) + onboardingStepVisitedSuffix;
  return dispatchWithConfiguredAnalytics(eventName, (analytics) => {
    logAnalyticsEvent(analytics, eventName, baseParams);
  });
}

/**
 * Log initial page view with UTM parameters
 * Should be called once when the app first loads
 */
export async function logPageView(
  pagePath?: string
): Promise<AnalyticsDispatchResult> {
  const baseParams = getBaseEventParameters();
  return dispatchWithConfiguredAnalytics("page_view", (analytics) => {
    logAnalyticsEvent(analytics, "page_view", {
      ...baseParams,
      page_path: pagePath || window.location.pathname,
      page_location: window.location.href,
    });
  });
}
